import { useAuth } from './useAuth';
import { withoutParentForChildLogin } from '../utils/userRoles';

/**
 * Result of checking whether the current authenticated user is allowed to
 * access the player-self portal (KI-010 §8.x).
 *
 * WHERE THE ANSWER COMES FROM (FIX-BACKLOG C13/C14)
 *
 * `isThirteenPlus` is a SERVER fact and nothing else. It is the
 * `/api/player-portal/access` probe's verdict, computed from
 * `players.date_of_birth` — a column only a club admin can write — at the
 * player's CLUB'S consent age (KAN-233: 13 US, 15 AU, 16 EU, 16 when the club's
 * country is not on record; the name is historical), and cached in AuthContext
 * for the session. Nothing here knows an age, so nothing here can disagree with
 * the server about one.
 *
 * It used to be read from `tenant.coppaConsent`, which AuthContext built from
 * Supabase `user_metadata`. That was broken twice over. Mechanically: nothing in
 * the product ever wrote that key, and the default was the boolean `false`, so
 * `.verified` was `undefined` and the condition could not be true for anyone —
 * all 7 portal routes were dead for every real user. Structurally: `user_metadata`
 * is user-writable (`supabase.auth.updateUser({ data: … })`), so the "obvious"
 * repair — write the field — would have let any child grant themselves 13+ status
 * on a COPPA gate. The claim had to move to the server, not merely be populated.
 *
 * FAIL-CLOSED CONTRACT. `portalAccess === null` means UNKNOWN — probe in flight,
 * probe failed, API down, user signed out. Every one of those denies. The only
 * state that admits is an explicit `canAccessPortal: true` from the server.
 *
 * Four load-bearing flags drive the wrapping route guard:
 *  - `isLoading`: auth is bootstrapping OR the access probe is in flight; render
 *     a spinner. Deciding during this window would show a legitimate 13+
 *     player the under-13 block before the server had answered.
 *  - `isAuthenticated`: user + tenant present.
 *  - `hasPlayerRole`: tenant.roles includes 'player'. From app_metadata, which is
 *     admin-only writable — trusted, unlike the old consent read.
 *  - `isThirteenPlus`: the server says this caller's own player record is at or
 *     over their club's consent age.
 *
 * And, for a caller who is NOT admitted, why not (KAN-63). `isThirteenPlus` is
 * false for a real under-13 player, for an unknown answer, for a player role with
 * no linked record, and for a parent whose record resolves to their child. The
 * guard shows each of those something different, so these pass on what the
 * server already said. None of them admits anyone; only `isThirteenPlus` does.
 *  - `hasParentRole`: tenant.roles includes 'parent' (app_metadata, as above), read the way
 *     ProtectedRoute reads it (KAN-143 withoutParentForChildLogin): a child's own login, 'player'
 *     with no admin or coach role, never holds 'parent', so the guard never sends it to
 *     /family-circle, which would turn it away to '/' (the KAN-63 dead end).
 *  - `accessKnown`: the probe answered. False when it failed (500, network).
 *  - `hasPlayerRecord` / `isSelfPlayer`: the server's own flags, read `=== true`.
 */
export interface SelfPlayerStatus {
  isLoading: boolean;
  isAuthenticated: boolean;
  hasPlayerRole: boolean;
  hasParentRole: boolean;
  /** The access probe answered. False means UNKNOWN, never "no". */
  accessKnown: boolean;
  /** A players row resolves for this caller in this org (server fact). */
  hasPlayerRecord: boolean;
  /** The caller IS that player, not a parent resolving their child (server fact). */
  isSelfPlayer: boolean;
  isThirteenPlus: boolean;
  /** Convenience: all gates pass — caller may render the portal. */
  canAccessPortal: boolean;
}

export function useIsSelfPlayer(): SelfPlayerStatus {
  const { user, tenant, loading, portalAccess, portalAccessLoading } = useAuth();

  if (loading || portalAccessLoading) {
    return {
      isLoading: true,
      isAuthenticated: false,
      hasPlayerRole: false,
      hasParentRole: false,
      accessKnown: false,
      hasPlayerRecord: false,
      isSelfPlayer: false,
      isThirteenPlus: false,
      canAccessPortal: false,
    };
  }

  const isAuthenticated = Boolean(user && tenant);
  const hasPlayerRole = Boolean(tenant?.roles.includes('player'));
  const hasParentRole = Boolean(
    tenant && withoutParentForChildLogin(tenant.roles).includes('parent'),
  );
  // `=== true` rather than truthiness: portalAccess is null while unknown, and an
  // unknown age must never read as an eligible one.
  const isThirteenPlus = portalAccess?.isThirteenPlus === true;

  return {
    isLoading: false,
    isAuthenticated,
    hasPlayerRole,
    hasParentRole,
    accessKnown: portalAccess !== null,
    hasPlayerRecord: portalAccess?.hasPlayerRecord === true,
    isSelfPlayer: portalAccess?.isSelfPlayer === true,
    isThirteenPlus,
    // canAccessPortal is the server's own composite, re-checked against the
    // client-side gates rather than replacing them. The server already requires
    // isSelfPlayer (the caller IS the player, not their parent); the role and
    // auth checks here keep the redirect targets meaningful.
    canAccessPortal: isAuthenticated && hasPlayerRole && portalAccess?.canAccessPortal === true,
  };
}
